'use client'

import { useMemo, useState } from 'react'

import {
  useSensors,
  useSensor,
  PointerSensor,
  KeyboardSensor,
  DndContext,
  rectIntersection,
  DragOverlay,
  defaultDropAnimationSideEffects
} from '@dnd-kit/core'
import { sortableKeyboardCoordinates, arrayMove } from '@dnd-kit/sortable'
import _ from 'lodash'
import { LoaderCircle } from 'lucide-react'
import { createPortal } from 'react-dom'

import { useBorderColor } from '../../../hooks/useBorderColor'
import { LevelFooter } from '../../Footer'
import { HUD } from '../../HUD'
import { LevelToolbar } from '../../Toolbar'

import { useLevelContext } from '@/features/level-play'
import { ChallengeStep, useFlow } from '@/shared/lib/steps-flow'
import { showErrorAlert } from '@/shared/lib/ui'
import { Button } from '@/shared/ui/Button'


import { AtThePlaceBoard } from './Board'
import { AtThePlaceGhostWord } from './Word'

interface GameAtThePlaceProps {
  step: ChallengeStep
  challengeId: number
}

type ItemsState = {
  rightAssociationsExpressions: string[]
  leftAssociationExpressions: string[]
}

export function GameAtThePlace({ challengeId }: GameAtThePlaceProps) {
  const {
    state,
    interactionDisabled,
    goNext,
    enableContinue,
    verifyChallenge,
    retryChallenge
  } = useFlow()
  const { level, journeyAllowWrongAnswers, isSubmitting, onChallengeAnswer } =
    useLevelContext()

  const { borderColor } = useBorderColor()

  const challenge = level?.active_challenges.find(c => c.id === challengeId)
  const meta = challenge?.meta as any | undefined
  const opts = challenge?.challenge_options ?? []
  const useOptions = opts.length >= 2
  const associations = useOptions
    ? opts.map(opt => ({
        name: opt.name ?? '',
        image: opt.meta?.image ?? undefined
      }))
    : (meta?.associations as any[]) ?? []

  const leftExpressions: string[] = useOptions
    ? (Array.isArray(opts[0]?.meta?.expressions)
        ? opts[0].meta.expressions.filter(Boolean)
        : []) as string[]
    : Array.isArray(meta?.associations?.[0]?.expressions)
      ? meta.associations[0].expressions.filter(Boolean)
      : []
  const rightExpressions: string[] = useOptions
    ? (Array.isArray(opts[1]?.meta?.expressions)
        ? opts[1].meta.expressions.filter(Boolean)
        : []) as string[]
    : Array.isArray(meta?.associations?.[1]?.expressions)
      ? meta.associations[1].expressions.filter(Boolean)
      : []

  const initialItems: ItemsState = useMemo(() => {
    const combined = [...leftExpressions, ...rightExpressions].filter(Boolean)
    const seed = hashString(String(challenge?.uid ?? challengeId))
    const shuffled = seededShuffle(combined, seed)
    const half = Math.ceil(shuffled.length / 2)

    return {
      rightAssociationsExpressions: shuffled.slice(0, half),
      leftAssociationExpressions: shuffled.slice(half)
    }
  }, [challenge?.uid, challengeId])

  const [items, setItems] = useState<ItemsState>(() => initialItems)
  const [activeId, setActiveId] = useState<string | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const findContainer = (id: string) => {
    if (id in items) return id
    return (Object.keys(items) as (keyof ItemsState)[]).find(key =>
      items[key].includes(id)
    )
  }

  const handleDragStart = (event: any) => {
    setActiveId(event.active.id)
  }

  const handleDragOver = ({ active, over }: any) => {
    const overId = over?.id
    if (overId == null || active.id in items) return

    const overContainer = findContainer(overId)
    const activeContainer = findContainer(active.id)
    if (!overContainer || !activeContainer || overContainer === activeContainer)
      return

    setItems(curr => {
      const activeItems = curr[activeContainer]
      const overItems = curr[overContainer]
      const overIndex = overItems.indexOf(overId)
      const activeIndex = activeItems.indexOf(active.id)

      let newIndex: number
      if (overId in curr) {
        newIndex = overItems.length + 1
      } else {
        const isBelowOverItem =
          over &&
          active.rect.current.translated &&
          active.rect.current.translated.top > over.rect.top + over.rect.height
        const modifier = isBelowOverItem ? 1 : 0
        newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1
      }

      return {
        ...curr,
        [activeContainer]: curr[activeContainer].filter(i => i !== active.id),
        [overContainer]: [
          ...curr[overContainer].slice(0, newIndex),
          curr[activeContainer][activeIndex],
          ...curr[overContainer].slice(newIndex)
        ]
      }
    })
  }

  const handleDragEnd = ({ active, over }: any) => {
    if (!over?.id) {
      setActiveId(null)
      return
    }

    const activeContainer = findContainer(active.id)
    const overContainer = findContainer(over.id)
    if (!activeContainer || !overContainer) {
      setActiveId(null)
      return
    }

    if (activeContainer === overContainer) {
      const container = activeContainer as keyof ItemsState
      const activeIndex = items[container].indexOf(active.id)
      const overIndex = items[container].indexOf(over.id)
      if (activeIndex !== overIndex) {
        setItems(curr => ({
          ...curr,
          [container]: arrayMove(curr[container], activeIndex, overIndex)
        }))
      }
    }
    setActiveId(null)
  }

  const validateAnswer = (s: ItemsState) => {
    const expectedLeft = leftExpressions
    const expectedRight = rightExpressions
    return (
      arraysEqualUnordered(expectedLeft, s.leftAssociationExpressions) &&
      arraysEqualUnordered(expectedRight, s.rightAssociationsExpressions)
    )
  }

  const handleVerify = async () => {
    const answerIsCorrect = validateAnswer(items)
    const skipValidation = Boolean(meta?.save_answer)
    const canAnswerChallenge =
      answerIsCorrect || skipValidation || journeyAllowWrongAnswers

    try {
      if (!challenge) return

      if (canAnswerChallenge) {
        await onChallengeAnswer({
          uid: challenge.uid,
          answer: JSON.stringify(items),
          answerIsCorrect,
          skipValidation
        })
        enableContinue()
      }
      verifyChallenge({ correct: answerIsCorrect || skipValidation })
    } catch (error) {
      showErrorAlert(error)
    }
  }

  const handleRetry = () => {
    retryChallenge()
  }

  return (
    <HUD.Card>
      <HUD.Header challenge={challenge} />
      <HUD.Toolbar>
        <LevelToolbar />
      </HUD.Toolbar>

      <HUD.Body className="flex flex-col gap-4">
        <HUD.Description challenge={challenge} />

        <DndContext
          sensors={sensors}
          collisionDetection={rectIntersection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="grid w-full mx-auto grid-cols-2 gap-1">
            {associations.map((association: any, index: number) => {
              const id =
                index === 0
                  ? 'leftAssociationExpressions'
                  : 'rightAssociationsExpressions'
              const imageUrl = _.get(association, 'image.file.url')
              const hasImage = Boolean(imageUrl)

              return (
                <div
                  key={`association-${index}`}
                  className={`overflow-hidden rounded-xl border-2 flex flex-col items-center ${borderColor} bg-white`}
                >
                  {hasImage ? (
                    <>
                      <div className="aspect-square w-full rounded-t-xl overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={association?.name ?? ''}
                          className="w-full h-full object-cover object-center bg-white"
                        />
                      </div>
                      <span className="block w-full p-4 text-center text-gray-800 font-bold text-base md:text-lg">
                        {association?.name}
                      </span>
                    </>
                  ) : (
                    <span
                      className="text-gray-800 font-bold text-base md:text-lg w-full text-center  p-4 min-h-[100px] h-full flex flex-col items-center justify-center"
                      style={{
                        wordBreak: 'break-word'
                      }}
                    >
                      {association?.name}
                    </span>
                  )}
                  <div className="flex w-full flex-grow gap-3">
                    <AtThePlaceBoard
                      key={id}
                      id={id}
                      items={
                        id === 'leftAssociationExpressions'
                          ? items.leftAssociationExpressions
                          : items.rightAssociationsExpressions
                      }
                      disabled={interactionDisabled}
                    />
                  </div>
                </div>
              )
            })}
          </div>

          {createPortal(
            <DragOverlay adjustScale={false} dropAnimation={dropAnimation}>
              <div style={{ touchAction: 'none' }}>
                {activeId ? <AtThePlaceGhostWord id={activeId} /> : null}
              </div>
            </DragOverlay>,
            document.body
          )}
        </DndContext>
      </HUD.Body>

      <LevelFooter>
        {state.phase === 'READY' && (
          <Button
            type="button"
            onClick={handleVerify}
            className="w-full font-bold"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <LoaderCircle className="size-5 animate-spin" />
                <span>Carregando</span>
              </>
            ) : (
              <span>Verificar</span>
            )}
          </Button>
        )}

        {state.phase === 'CORRECT' && (
          <Button
            type="button"
            onClick={goNext}
            className="w-full font-bold"
            disabled={isSubmitting}
          >
            <span>Continuar</span>
          </Button>
        )}

        {state.phase === 'LOCKED_WRONG' && (
          <Button
            type="button"
            onClick={journeyAllowWrongAnswers ? goNext : handleRetry}
            className="w-full font-bold"
            disabled={isSubmitting}
          >
            <span>
              {journeyAllowWrongAnswers ? 'Entendido' : 'Tentar novamente'}
            </span>
          </Button>
        )}
      </LevelFooter>
    </HUD.Card>
  )
}

const hashString = (s: string) => {
  let h = 5381
  for (let i = 0; i < s.length; i++) h = (h * 33) ^ s.charCodeAt(i)
  return h >>> 0
}
const mulberry32 = (a: number) => () => {
  a |= 0
  a = (a + 0x6d2b79f5) | 0
  let t = Math.imul(a ^ (a >>> 15), 1 | a)
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296
}
const seededShuffle = <T,>(list: T[], seed: number): T[] => {
  const rnd = mulberry32(seed)
  const a = list.slice()
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1))
    ;[a[i], a[j]] = [a[j], a[i]]
  }
  return a
}
const arraysEqualUnordered = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false
  const aa = [...a].sort()
  const bb = [...b].sort()
  for (let i = 0; i < aa.length; i++) if (aa[i] !== bb[i]) return false
  return true
}

const dropAnimation = {
  sideEffects: defaultDropAnimationSideEffects({
    styles: { active: { opacity: '0.5' } }
  })
}
